import { useState, useEffect, useCallback, useRef, FormEvent, ChangeEvent } from 'react';
import RateService from '../../AppSettings/services/RateService';
import BillingCategoryService from '../../AppSettings/services/BillingCategoryService';
import toastService from '../../AppSettings/services/ToastService';
import { useError } from '../../Context/ErrorContext';
import { CostCodesManager } from './CostCodesModal';
import type { Rate, NewRate, ErrorContextValue } from '../../types/team';
import type { BillingCategory } from '../../Models/Rate/RateModels';

type BillingPane = 'rates' | 'cost-codes';

interface NewRateWithCategories extends NewRate {
  assoc_categories: string[];
}

interface CompanyRatesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function parseCategoryIds(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

function isNumericRate(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return false;
  return Number.isFinite(Number(trimmed));
}

function CategoryMultiSelect({
  categories,
  selected,
  onChange,
  disabled,
}: {
  categories: BillingCategory[];
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (id: string) => {
    onChange(
      selected.includes(id)
        ? selected.filter((s) => s !== id)
        : [...selected, id],
    );
  };

  const selectedLabels = categories
    .filter((c) => selected.includes(c.categoryid))
    .map((c) => c.category);

  return (
    <div className="team-rates-category-select" ref={ref}>
      <button
        type="button"
        className="team-rates-category-trigger"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
      >
        <span className="team-rates-category-trigger-text">
          {selectedLabels.length > 0
            ? selectedLabels.join(', ')
            : 'Select categories...'}
        </span>
        <span className="team-rates-category-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="team-rates-category-dropdown">
          {categories.length === 0 ? (
            <div className="team-rates-category-empty">No categories available</div>
          ) : (
            categories.map((cat) => (
              <label key={cat.categoryid} className="team-rates-category-option">
                <input
                  type="checkbox"
                  checked={selected.includes(cat.categoryid)}
                  onChange={() => toggle(cat.categoryid)}
                />
                <span className="team-rates-category-code">{cat.categoryid}</span>
                <span>{cat.category}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const CompanyRatesModal = ({
  isOpen,
  onClose,
}: CompanyRatesModalProps): JSX.Element | null => {
  const [rates, setRates] = useState<Rate[]>([]);
  const [ratesLoading, setRatesLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<BillingCategory[]>([]);
  const [newRate, setNewRate] = useState<NewRateWithCategories>({
    rate_title: '',
    rate: '',
    assoc_categories: [],
  });
  const [rateSubmitting, setRateSubmitting] = useState<boolean>(false);
  const [editingRateId, setEditingRateId] = useState<string | null>(null);
  const [editingRate, setEditingRate] = useState<NewRateWithCategories>({
    rate_title: '',
    rate: '',
    assoc_categories: [],
  });
  const [expandedRateId, setExpandedRateId] = useState<string | null>(null);
  const [pane, setPane] = useState<BillingPane>('rates');

  const { setError } = useError() as unknown as ErrorContextValue;

  const fetchRates = useCallback(async (): Promise<void> => {
    setRatesLoading(true);
    try {
      const data = await RateService.getRates();
      setRates(data);
    } catch (error) {
      console.error('Error fetching rates:', error);
    } finally {
      setRatesLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async (): Promise<void> => {
    try {
      const data = await BillingCategoryService.getCategories();
      setCategories(data);
      const validIds = new Set(data.map((category) => category.categoryid));
      setNewRate((prev) => ({
        ...prev,
        assoc_categories: prev.assoc_categories.filter((id) => validIds.has(id)),
      }));
      setEditingRate((prev) => ({
        ...prev,
        assoc_categories: prev.assoc_categories.filter((id) => validIds.has(id)),
      }));
    } catch (error) {
      console.error('Error fetching billing categories:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setPane('rates');
      fetchRates();
      fetchCategories();
    }
  }, [isOpen, fetchRates, fetchCategories]);

  const handleCreateRate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!newRate.rate_title.trim() || !newRate.rate.trim()) {
      return;
    }
    if (!isNumericRate(newRate.rate)) {
      toastService.error('Rate must be a number. Do not include currency symbols or other characters.');
      return;
    }
    setRateSubmitting(true);
    try {
      await RateService.createRate({
        rate_title: newRate.rate_title.trim(),
        rate: newRate.rate.trim(),
        assoc_categories:
          newRate.assoc_categories.length > 0
            ? newRate.assoc_categories.join(',')
            : undefined,
      });
      setNewRate({ rate_title: '', rate: '', assoc_categories: [] });
      await fetchRates();
    } catch (error) {
      console.error('Error creating rate:', error);
      setError(error);
    } finally {
      setRateSubmitting(false);
    }
  };

  const handleDeleteRate = async (rateId: string): Promise<void> => {
    try {
      await RateService.deleteRate(rateId);
      await fetchRates();
    } catch (error) {
      console.error('Error deleting rate:', error);
      setError(error);
    }
  };

  const handleStartEditRate = (rate: Rate): void => {
    setEditingRateId(rate.rate_id);
    setEditingRate({
      rate_title: rate.rate_title,
      rate: rate.rate,
      assoc_categories: parseCategoryIds(rate.assoc_categories),
    });
  };

  const handleCancelEditRate = (): void => {
    setEditingRateId(null);
    setEditingRate({ rate_title: '', rate: '', assoc_categories: [] });
  };

  const handleSaveEditRate = async (rateId: string): Promise<void> => {
    if (!editingRate.rate_title.trim() || !editingRate.rate.trim()) {
      return;
    }
    if (!isNumericRate(editingRate.rate)) {
      toastService.error('Rate must be a number. Do not include currency symbols or other characters.');
      return;
    }
    try {
      await RateService.updateRate(rateId, {
        rate_title: editingRate.rate_title.trim(),
        rate: editingRate.rate.trim(),
        assoc_categories:
          editingRate.assoc_categories.length > 0
            ? editingRate.assoc_categories.join(',')
            : '',
      });
      setEditingRateId(null);
      setEditingRate({ rate_title: '', rate: '', assoc_categories: [] });
      await fetchRates();
    } catch (error) {
      console.error('Error updating rate:', error);
      setError(error);
    }
  };

  const categoryLabel = (categoryIds: string | null | undefined): string => {
    const ids = parseCategoryIds(categoryIds);
    if (ids.length === 0) return '—';
    return ids.join(', ');
  };

  const categoryFullLabel = (categoryIds: string | null | undefined): string[] => {
    const ids = parseCategoryIds(categoryIds);
    return ids.map((id) => {
      const cat = categories.find((c) => c.categoryid === id);
      return cat ? `${cat.categoryid} – ${cat.category}` : id;
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="team-rates-modal-overlay">
      <div className="team-rates-modal team-rates-modal--billing">
        <div className="team-rates-modal-header team-rates-modal-header--split">
          <div className="team-rates-modal-header-top">
            <div>
              <h3>Company Rates</h3>
              <p className="team-rates-modal-subtitle">
                Hourly rates and the cost codes they can be billed against.
              </p>
            </div>
            <button className="team-rates-modal-close" onClick={onClose}>
              ×
            </button>
          </div>
          <div
            className="team-rates-pane-switch"
            role="tablist"
            aria-label="Company rates sections"
          >
            <button
              type="button"
              role="tab"
              aria-selected={pane === 'rates'}
              className={pane === 'rates' ? 'active' : ''}
              onClick={() => {
                setPane('rates');
                fetchCategories();
              }}
            >
              Rates
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={pane === 'cost-codes'}
              className={pane === 'cost-codes' ? 'active' : ''}
              onClick={() => setPane('cost-codes')}
            >
              Cost Codes
            </button>
          </div>
        </div>

        <div className="team-rates-modal-body">
          {pane === 'cost-codes' ? (
            <CostCodesManager onChanged={fetchCategories} />
          ) : (
            <>
          <form onSubmit={handleCreateRate} className="team-rates-form">
            <div className="team-rates-form-row">
              <input
                type="text"
                placeholder="Rate Title (e.g., Senior Associate)"
                value={newRate.rate_title}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setNewRate({ ...newRate, rate_title: e.target.value })
                }
                required
                className="team-rates-input"
              />
              <input
                type="text"
                placeholder="Rate (e.g., 150.00)"
                value={newRate.rate}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setNewRate({ ...newRate, rate: e.target.value })
                }
                required
                className="team-rates-input team-rates-input-rate"
              />
            </div>
            <div className="team-rates-form-row team-rates-form-row--categories">
              <div className="team-rates-category-field">
                <CategoryMultiSelect
                  categories={categories}
                  selected={newRate.assoc_categories}
                  onChange={(ids) =>
                    setNewRate({ ...newRate, assoc_categories: ids })
                  }
                />
                <button
                  type="button"
                  className="team-rates-manage-codes"
                  onClick={() => setPane('cost-codes')}
                >
                  Add or manage cost codes
                </button>
              </div>
              <button
                type="submit"
                className="team-rates-add-button"
                disabled={rateSubmitting}
              >
                {rateSubmitting ? 'Adding...' : 'Add Rate'}
              </button>
            </div>
          </form>

          <div className="team-rates-list">
            {ratesLoading ? (
              <div className="team-rates-loading">Loading rates...</div>
            ) : rates.length === 0 ? (
              <div className="team-rates-empty">
                No rates configured yet. Add your first rate above.
              </div>
            ) : (
              <table className="team-rates-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Rate</th>
                    <th>Categories</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((rate) => (
                    <tr key={rate.rate_id}>
                      {editingRateId === rate.rate_id ? (
                        <>
                          <td>
                            <input
                              type="text"
                              value={editingRate.rate_title}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setEditingRate({
                                  ...editingRate,
                                  rate_title: e.target.value,
                                })
                              }
                              className="team-rates-edit-input"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={editingRate.rate}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setEditingRate({
                                  ...editingRate,
                                  rate: e.target.value,
                                })
                              }
                              className="team-rates-edit-input team-rates-edit-input-rate"
                            />
                          </td>
                          <td>
                            <CategoryMultiSelect
                              categories={categories}
                              selected={editingRate.assoc_categories}
                              onChange={(ids) =>
                                setEditingRate({
                                  ...editingRate,
                                  assoc_categories: ids,
                                })
                              }
                            />
                          </td>
                          <td>
                            <div className="team-rates-action-buttons">
                              <button
                                className="team-rates-save-button"
                                onClick={() => handleSaveEditRate(rate.rate_id)}
                              >
                                Save
                              </button>
                              <button
                                className="team-rates-cancel-button"
                                onClick={handleCancelEditRate}
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{rate.rate_title}</td>
                          <td>${rate.rate}</td>
                          <td className="team-rates-categories-cell">
                            {parseCategoryIds(rate.assoc_categories).length > 0 ? (
                              <div
                                className="team-rates-categories-toggle"
                                onClick={() =>
                                  setExpandedRateId(
                                    expandedRateId === rate.rate_id ? null : rate.rate_id,
                                  )
                                }
                              >
                                <span>{categoryLabel(rate.assoc_categories)}</span>
                                {expandedRateId === rate.rate_id && (
                                  <div className="team-rates-categories-expanded">
                                    {categoryFullLabel(rate.assoc_categories).map((label) => (
                                      <div key={label} className="team-rates-categories-expanded-item">
                                        {label}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            <div className="team-rates-action-buttons">
                              <button
                                className="team-rates-edit-button"
                                onClick={() => handleStartEditRate(rate)}
                              >
                                Edit
                              </button>
                              <button
                                className="team-rates-delete-button"
                                onClick={() => handleDeleteRate(rate.rate_id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyRatesModal;
